class TrendyolProfitCalculator {
  constructor() {
    // Form elements
    this.form = document.getElementById('trendyol-profit-form');
    this.categorySearch = document.getElementById('trendyol-category-search');
    this.commissionRateInput = document.getElementById('trendyol-commission-rate');
    this.purchasePrice = document.getElementById('trendyol-purchase-price');
    this.salePrice = document.getElementById('trendyol-sale-price');
    this.customVatInput = document.getElementById('trendyol-custom-vat');
    this.shippingCost = document.getElementById('trendyol-shipping-cost');
    
    // VAT buttons
    this.vatButtons = document.querySelectorAll('.trendyol-vat-btn');
    this.customVatContainer = document.querySelector('.trendyol-custom-vat');
    
    // Radio buttons
    this.shippingPayment = document.querySelectorAll('input[name="shipping-payment"]');
    
    // KDV toggle switches
    this.purchasePriceKdvInclusive = document.getElementById('purchase-price-kdv-inclusive');
    this.shippingCostKdvInclusive = document.getElementById('shipping-cost-kdv-inclusive');
    
    // Results elements
    this.noResults = document.querySelector('.phone-no-results');
    this.hasResults = document.querySelector('.phone-has-results');
    
    // Result display elements
    this.salePriceIncVat = document.getElementById('sale-price-inc-vat');
    this.salePriceExcVat = document.getElementById('sale-price-exc-vat');
    this.purchasePriceDisplay = document.getElementById('purchase-price');
    this.commissionRateDisplay = document.getElementById('commission-rate-display');
    this.commissionAmount = document.getElementById('commission-amount');
    this.shippingAmount = document.getElementById('shipping-amount');
    this.netProfit = document.getElementById('net-profit');
    this.profitMargin = document.getElementById('profit-margin');
    
    // Commission data will be loaded from JSON
    this.commissionData = [];
    this.categoryRates = {};
    
    // Current VAT rate
    this.currentVatRate = 20;
    
    this.init();
  }

  init() {
    this.loadCommissionData();
    this.bindEvents();
  }

  async loadCommissionData() {
    try {
      // Use the URL provided by Shopify's asset system
      const dataUrl = window.trendyolCommissionDataUrl || '/data/trendyol.json';
      const response = await fetch(dataUrl);
      if (!response.ok) throw new Error('Failed to load commission data');
      
      this.commissionData = await response.json();
      
      // Build category rates mapping and update datalist
      this.updateDatalist();
      this.buildCategoryRates();
    } catch (error) {
      console.error('Error loading commission data:', error);
    }
  }

  updateDatalist() {
    // Get the datalist element
    const datalist = document.getElementById('trendyol-categories');
    if (!datalist) return;
    
    // Clear existing options
    datalist.innerHTML = '';
    
    // Create unique categories list
    const uniqueCategories = new Set();
    
    this.commissionData.forEach(item => {
      // Create the search string as mainCategory > category > productGroupDetail
      const searchString = `${item.mainCategory} > ${item.category} > ${item.productGroupDetail}`;
      uniqueCategories.add(searchString);
    });
    
    // Add options to datalist
    uniqueCategories.forEach(category => {
      const option = document.createElement('option');
      option.value = category;
      datalist.appendChild(option);
    });
  }

  buildCategoryRates() {
    // Build mapping of category search strings to commission rates
    this.categoryRates = {};
    
    this.commissionData.forEach(item => {
      const searchString = `${item.mainCategory} > ${item.category} > ${item.productGroupDetail}`;
      this.categoryRates[searchString] = item.commissionRate;
    });
  }

  bindEvents() {
    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.calculateProfit();
    });

    // VAT button selection
    this.vatButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectVatRate(btn);
      });
    });

    // Custom VAT input
    this.customVatInput.addEventListener('input', () => {
      this.currentVatRate = parseFloat(this.customVatInput.value) || 0;
    });

    // Category change updates commission rate
    this.categorySearch.addEventListener('input', () => {
      this.updateCommissionRateFromCategory();
    });
  }

  selectVatRate(selectedBtn) {
    // Remove active class from all buttons
    this.vatButtons.forEach(btn => btn.classList.remove('active'));
    
    // Add active class to selected button
    selectedBtn.classList.add('active');
    
    const vatValue = selectedBtn.dataset.vat;
    
    if (vatValue === 'custom') {
      this.customVatContainer.style.display = 'block';
      this.currentVatRate = parseFloat(this.customVatInput.value) || 18;
    } else {
      this.customVatContainer.style.display = 'none';
      this.currentVatRate = parseFloat(vatValue);
    }
  }

  updateCommissionRateFromCategory() {
    const selectedCategory = this.categorySearch.value;
    const rate = this.categoryRates[selectedCategory];
    
    if (rate !== undefined) {
      this.commissionRateInput.value = rate;
    }
  }

  getCommissionRate() {
    // Get commission rate from input field, default to 10%
    return parseFloat(this.commissionRateInput.value) || 10.0;
  }

  calculateProfit() {
    // Get form values
    const category = this.categorySearch.value.trim();
    const commissionRate = parseFloat(this.commissionRateInput.value) || 0;
    const purchasePriceInput = parseFloat(this.purchasePrice.value) || 0;
    const salePrice = parseFloat(this.salePrice.value) || 0;
    const shippingCostInput = parseFloat(this.shippingCost.value) || 0;
    const shippingPayment = document.querySelector('input[name="shipping-payment"]:checked')?.value || 'seller';
    
    // Check KDV inclusion toggles
    const isPurchasePriceKdvInclusive = this.purchasePriceKdvInclusive.checked;
    const isShippingCostKdvInclusive = this.shippingCostKdvInclusive.checked;

    // Validate inputs
    if (!category || commissionRate <= 0 || purchasePriceInput <= 0 || salePrice <= 0) {
      this.showNoResults();
      return;
    }

    // Calculate purchase price excluding VAT
    const purchasePrice = isPurchasePriceKdvInclusive 
      ? purchasePriceInput / (1 + this.currentVatRate / 100)
      : purchasePriceInput;

    // Calculate shipping cost excluding VAT for base amount
    const shippingCostBase = isShippingCostKdvInclusive 
      ? shippingCostInput / (1 + this.currentVatRate / 100)
      : shippingCostInput;

    // Calculate VAT amounts for sale price
    const salePriceExcludingVat = salePrice / (1 + this.currentVatRate / 100);
    const salePriceIncludingVat = salePrice;

    // Calculate commission (on price excluding VAT)
    const commissionAmount = salePriceExcludingVat * (commissionRate / 100);

    // Calculate final shipping cost (only apply if seller pays)
    let finalShippingCost = 0;
    if (shippingPayment === 'seller') {
      // Seller pays shipping - use the calculated shipping cost
      finalShippingCost = shippingCostBase;
    }

    // Calculate net profit
    const netProfit = salePriceExcludingVat - purchasePrice - commissionAmount - finalShippingCost;

    // Calculate profit margin
    const profitMargin = salePriceExcludingVat > 0 ? (netProfit / salePriceExcludingVat) * 100 : 0;

    // Display results
    this.displayResults({
      salePriceIncVat: salePriceIncludingVat,
      salePriceExcVat: salePriceExcludingVat,
      purchasePrice: purchasePrice, // Show the calculated purchase price (excluding VAT)
      commissionRate: commissionRate,
      commissionAmount: commissionAmount,
      shippingAmount: finalShippingCost,
      netProfit: netProfit,
      profitMargin: profitMargin
    });
  }

  displayResults(results) {
    // Update result values
    this.salePriceIncVat.textContent = this.formatCurrency(results.salePriceIncVat);
    this.salePriceExcVat.textContent = this.formatCurrency(results.salePriceExcVat);
    this.purchasePriceDisplay.textContent = '- ' + this.formatCurrency(results.purchasePrice);
    this.commissionRateDisplay.textContent = `(%${results.commissionRate.toFixed(1)})`;
    this.commissionAmount.textContent = '- ' + this.formatCurrency(results.commissionAmount);
    this.shippingAmount.textContent = results.shippingAmount > 0 ? '- ' + this.formatCurrency(results.shippingAmount) : '0.00 ₺';
    
    // Format profit/loss
    if (results.netProfit >= 0) {
      this.netProfit.textContent = this.formatCurrency(results.netProfit);
      this.netProfit.style.color = '#10b981'; // Green for profit
    } else {
      this.netProfit.textContent = '- ' + this.formatCurrency(Math.abs(results.netProfit));
      this.netProfit.style.color = '#ef4444'; // Red for loss
    }
    
    // Format margin
    if (results.profitMargin >= 0) {
      this.profitMargin.textContent = results.profitMargin.toFixed(2) + ' %';
      this.profitMargin.style.color = '#f27a1a'; // Trendyol orange
    } else {
      this.profitMargin.textContent = results.profitMargin.toFixed(2) + ' %';
      this.profitMargin.style.color = '#ef4444'; // Red for negative margin
    }

    // Show results
    this.showResults();
  }

  showResults() {
    this.noResults.style.display = 'none';
    this.hasResults.style.display = 'block';
  }

  showNoResults() {
    this.noResults.style.display = 'block';
    this.hasResults.style.display = 'none';
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount)) + ' ₺';
  }

  // Utility method to validate form
  validateForm() {
    const category = this.categorySearch.value.trim();
    const commissionRate = parseFloat(this.commissionRateInput.value) || 0;
    const purchasePrice = parseFloat(this.purchasePrice.value) || 0;
    const salePrice = parseFloat(this.salePrice.value) || 0;
    
    return category && commissionRate > 0 && purchasePrice > 0 && salePrice > 0;
  }

  // Method to update datalist with more categories
  updateCategoryOptions(categories) {
    const datalist = document.getElementById('trendyol-categories');
    datalist.innerHTML = '';
    
    Object.keys(categories).forEach(category => {
      const option = document.createElement('option');
      option.value = category;
      datalist.appendChild(option);
    });
    
    this.categoryRates = { ...this.categoryRates, ...categories };
  }

  // Method to get current calculation state
  getCurrentState() {
    return {
      category: this.categorySearch.value,
      commissionRate: parseFloat(this.commissionRateInput.value) || 0,
      purchasePrice: parseFloat(this.purchasePrice.value) || 0,
      salePrice: parseFloat(this.salePrice.value) || 0,
      vatRate: this.currentVatRate,
      shippingCost: parseFloat(this.shippingCost.value) || 0,
      shippingPayment: document.querySelector('input[name="shipping-payment"]:checked')?.value || 'seller',
      purchasePriceKdvInclusive: this.purchasePriceKdvInclusive.checked,
      shippingCostKdvInclusive: this.shippingCostKdvInclusive.checked
    };
  }
}